/* eslint-disable @typescript-eslint/no-explicit-any */

import { Archive, CheckCircle2, Clock, ExternalLink, Link2, UserPlus } from 'lucide-react';
import { useWorkspacePresence } from '../../hooks/useWorkspacePresence';
import { threadAnalysisNotice, threadDisplayTitle } from '../../lib/clinic-logic';
import { mailThreadLink } from '../../lib/mail-links';
import { getActiveWorkspaceId, trpc } from '../../lib/trpc';
import { cn } from '../../lib/utils';
import type { EmailThreadMetadata, LabelCategoryMapping, TriageCategory } from '../../types/email';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { UserAvatar } from '../../ui/user-avatar';
import { AnalysisNotice } from './AnalysisNotice';
import { LabelBadgeList } from './LabelBadgeList';

export interface TriageThreadRowProps {
  thread: EmailThreadMetadata; // Allow extended type with contact property, mirroring TriageThreadCard
  labelMap?: Record<string, string>;
  categories?: TriageCategory[];
  mappings?: LabelCategoryMapping[];
  workspaceMembers?: Array<{ id: number; email: string; fullName: string | null }>;
  onArchive?: (threadId: string) => void;
  onDelegate?: (threadId: string) => void;
  onCreateTask?: (threadId: string) => void;
  onTaskClick?: (taskId: number) => void;
  onCardClick?: (threadId: string) => void;
  /** Re-run analysis for one thread; shown only when the failure is retryable. */
  onRetryAnalysis?: (threadId: string) => void;
  isReadOnly?: boolean;
}

// Difficulty, without alarm. Three steps of presence rather than three hues: the
// harder something is, the more solid its mark. Red would say "emergency"; this says
// "this one will take a while".
const DIFFICULTY_DOT: Record<string, string> = {
  Hard: 'bg-foreground',
  Moderate: 'bg-muted-foreground',
  Easy: 'bg-border',
};

/**
 * Single-line thread row for the Triage dense view — the compact counterpart to
 * `TriageThreadCard`. Same props/callbacks so the Triage views can swap between
 * the two purely on density.
 */
export function TriageThreadRow({
  thread,
  labelMap = {},
  categories = [],
  mappings = [],
  workspaceMembers = [],
  onArchive,
  onDelegate,
  onCreateTask,
  onTaskClick,
  onCardClick,
  onRetryAnalysis,
  isReadOnly = false,
}: TriageThreadRowProps) {
  const { url: mailUrl, label: mailLinkLabel } = mailThreadLink(thread);
  const task = thread.task;

  const meQuery = trpc.auth.me.useQuery();
  const presence = useWorkspacePresence(getActiveWorkspaceId(), meQuery.data?.uid ?? null);
  const viewers = (presence.usersByContext[thread.threadId] || []).filter(
    (id) => id !== meQuery.data?.uid
  );

  const labels = (thread.labels || []).filter(
    (l: string) => typeof l === 'string' && l !== 'YELLOW_STAR'
  );
  const isStarred = (thread.labels || []).includes('YELLOW_STAR');
  const displayTitle = threadDisplayTitle(thread);
  const analysisNotice = threadAnalysisNotice(thread);

  return (
    <div
      className={cn(
        'group flex items-center gap-2 border-border border-b px-2 py-2 transition-colors',
        thread.resolvedAt && 'opacity-60'
      )}
    >
      <span
        className={cn(
          'h-2 w-2 shrink-0 rounded-full',
          (thread.difficultyLevel && DIFFICULTY_DOT[thread.difficultyLevel]) || 'bg-border'
        )}
        title={analysisNotice ?? `${thread.difficultyLevel ?? ''} · ${thread.domainCategory ?? ''}`}
      />

      <button
        type="button"
        onClick={() => onCardClick?.(thread.threadId)}
        className="min-w-0 max-w-[40%] shrink-0 truncate text-left font-display text-sm font-normal hover:text-primary hover:underline"
        title={displayTitle}
      >
        {isStarred && <span className="mr-1">⭐</span>}
        {displayTitle}
      </button>

      {/* The core ask when we have one; otherwise say why we don't, rather than
 leaving a blank column the user cannot interpret. */}
      {analysisNotice ? (
        <AnalysisNotice
          thread={thread}
          onRetry={onRetryAnalysis}
          className="min-w-0 flex-1 overflow-hidden"
        />
      ) : (
        <span
          className="min-w-0 flex-1 truncate text-xs text-muted-foreground"
          title={thread.coreAsk ?? undefined}
        >
          {thread.coreAsk}
        </span>
      )}

      <div className="hidden shrink-0 items-center gap-1 xl:flex">
        {thread.contact && (
          <Badge
            variant="outline"
            className="h-5 gap-1 border-border bg-muted px-1.5 text-[10px] font-medium text-muted-foreground"
          >
            <Link2 className="h-2.5 w-2.5 shrink-0" />
            <span className="max-w-[80px] truncate">{thread.contact.label}</span>
          </Badge>
        )}
        <LabelBadgeList
          thread={thread}
          labels={labels}
          labelMap={labelMap}
          categories={categories}
          mappings={mappings}
          maxVisible={2}
        />
      </div>

      {viewers.length > 0 && (
        <div className="flex shrink-0 -space-x-1.5" title="Currently viewing this thread">
          {viewers.map((userId) => (
            <UserAvatar
              key={userId}
              user={workspaceMembers.find((m) => m.id === userId)}
              size="xs"
              className="border border-background"
            />
          ))}
        </div>
      )}

      {thread.resolvedAt && (
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-foreground" aria-label="Resolved" />
      )}

      {/* Always visible, not hover-revealed: whether a thread is already prescribed
 is a scanning signal, not an action. */}
      {task && (
        <button
          type="button"
          onClick={() => onTaskClick?.(task.id)}
          disabled={!onTaskClick}
          className={cn(
            'flex h-5 shrink-0 items-center gap-1 rounded-full border border-border bg-muted px-1.5 text-[10px] font-medium text-foreground',
            onTaskClick && 'hover:bg-muted'
          )}
          title={`Task: ${task.label} (${task.status})${
            task.assigneeName ? ` · ${task.assigneeName}` : ''
          }`}
        >
          <CheckCircle2 className="h-2.5 w-2.5" />
          <span className="hidden max-w-[90px] truncate lg:inline">{task.label}</span>
        </button>
      )}

      {/* Width is held even with no estimate, so rows stay aligned in a list that
 mixes analysed and unanalysed threads. */}
      <div className="flex w-14 shrink-0 items-center justify-end gap-1 text-[11px] text-muted-foreground tabular-nums">
        {thread.estimatedEffortMinutes !== null && thread.estimatedEffortMinutes !== undefined && (
          <>
            <Clock className="h-3 w-3" />
            {thread.estimatedEffortMinutes}m
          </>
        )}
      </div>

      {/* Fixed width so the row doesn't reflow as actions fade in on hover. */}
      <div className="flex w-[104px] shrink-0 items-center justify-end gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
        {onArchive && !isReadOnly && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => onArchive(thread.threadId)}
            title="Archive thread"
          >
            <Archive className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground" asChild>
          <a href={mailUrl} target="_blank" rel="noopener noreferrer" title={mailLinkLabel}>
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </Button>
        {onDelegate && !isReadOnly && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => onDelegate(thread.threadId)}
            title="Delegate thread"
          >
            <UserPlus className="h-3.5 w-3.5" />
          </Button>
        )}
        {onCreateTask && !thread.task && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-primary"
            onClick={() => onCreateTask(thread.threadId)}
            disabled={isReadOnly}
            title={isReadOnly ? 'Read-only workspace' : 'Prescribe'}
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
